import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Container, Paper, Typography, Box } from '@mui/material';
import { GoogleSheetsService, Show } from '../services/GoogleSheetsService';
import LoadingOverlay from 'react-loading-overlay-ts';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface ShowResultRow {
  showId: string;
  // Add other fields as needed
}

const ShowResultsManagementPage: React.FC = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const [shows, setShows] = useState<Show[]>([]);
  const [showResults, setShowResults] = useState<Record<string, ShowResultRow[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
         if (!user.user) throw new Error('User not authenticated');
         const service = await GoogleSheetsService.fromFirebaseUser(user.user);
        if (!service) throw new Error('Failed to initialize Google Sheets service');
        const allShows = await service.getAllShows();
        setShows(allShows);
        // TODO: Fetch show results for all shows (by showId)
        // Example: const results = await service.getAllShowResults();
        // setShowResults(resultsByShowId);
      } catch (err) {
        setError('Failed to load shows or results.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  return (
    <LoadingOverlay active={loading} className="loading-overlay">
      <Container maxWidth="md">
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2, mb: 2 }}>
          Back
        </Button>
        <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
            Show Results Management
          </Typography>
          {error && <Typography color="error">{error}</Typography>}
          <Box>
            {shows.length === 0 && !loading && (
              <Typography>No shows found.</Typography>
            )}
            {shows.filter(show => show.showName && show.showName.trim() !== '').map(show => {
              const hasResults = showResults[show.showId] && showResults[show.showId].length > 0;
              // Format date as mm/dd/yy
              let formattedDate = '';
              if (show.showDateTime) {
                const d = moment(show.showDateTime);
                formattedDate = d.isValid() ? d.format('MM/DD/YY') : show.showDateTime;
              }
              return (
                <Box key={show.showId} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">
                      {formattedDate} — {show.showName}
                    </Typography>
                  </Box>
                  {hasResults ? (
                    <Button variant="outlined" color="primary" onClick={() => navigate(`/admin/showResultsManagement/${show.showId}`)}>Edit Results</Button>
                  ) : (
                    <Button variant="contained" color="primary" onClick={() => navigate(`/admin/showResultsManagement/${show.showId}`)}>Add Results</Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Container>
    </LoadingOverlay>
  );
};

export default ShowResultsManagementPage;
